import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  estimateMicros,
  FILM_STOCKS,
  NO_STOCK_ID,
  type Garment,
  type ModelWithStats,
  type Shot,
  type ShootSettings,
} from '@studio/shared';

import {
  api,
  assetUrl,
  type StylistConversation,
  type StylistFinishes,
  type StylistMessage,
  type StylistOption,
  type StylistStep,
  type StylistSummary,
} from '../lib/api';
import { useDateFormat } from '../lib/dates';
import { useErrorText } from '../lib/errors';
import { useI18n, usePresetLabel, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { DEFAULT_SETTINGS } from '../lib/shootDefaults';
import { exampleUrl, useExamples } from '../lib/useExamples';
import { ClothesGuide } from '../components/ClothesGuide';
import { CheckIcon, PlusIcon, SlidersIcon } from '../components/Icons';
import { Sheet } from '../components/Sheet';
import { useToast } from '../components/Toaster';
import { ModelEditor } from './Models';
import { ApertureField, Dial, portraitOf, Thumb } from './Wizard';

const STEPS: StylistStep[] = ['outfit', 'place', 'pose'];
type Fiche = 'model' | 'pieces' | StylistStep;
const FICHE: Fiche[] = ['model', 'pieces', ...STEPS];

/**
 * "Describe the shoot": a conversation with the studio's stylist.
 *
 * The client can write at any point — "Inès in the embroidered jacket", "the
 * 2", "rather at the crossing", "more blur behind her", "the sleeves
 * shorter" — or tap: the stylist's three proposals for each step come as
 * cards, with room for an idea of one's own. What is decided stays in the
 * fiche at the top, and every version stays in the conversation.
 */
export function StylistPage() {
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();
  const money = useMoney();
  const when = useDateFormat();
  const describe = useErrorText();
  const [params, setParams] = useSearchParams();
  const id = params.get('c');

  const [conv, setConv] = useState<StylistConversation | undefined>();
  const [recent, setRecent] = useState<StylistSummary[]>([]);
  const [models, setModels] = useState<ModelWithStats[]>([]);
  const [wardrobe, setWardrobe] = useState<Garment[]>([]);
  const [picked, setPicked] = useState<Garment[]>([]);
  const [busy, setBusy] = useState<string | false>(false);
  const [error, setError] = useState('');
  const [text, setText] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [creatingModel, setCreatingModel] = useState(false);
  const [adding, setAdding] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);

  const loadModels = () =>
    api
      .listModels()
      .then((list) => {
        setModels(list);
        return list;
      })
      .catch(() => [] as ModelWithStats[]);

  useEffect(() => {
    void loadModels();
    void api
      .listGarments()
      .then(setWardrobe)
      .catch(() => undefined);
    void api.stylist
      .recent()
      .then(setRecent)
      .catch(() => undefined);
  }, []);

  // The conversation lives in the address, so a reload comes back to it.
  useEffect(() => {
    let live = true;
    setError('');
    const opened = id ? api.stylist.get(id) : api.stylist.start(locale === 'en' ? 'en' : 'fr');
    opened
      .then((loaded) => {
        if (!live) return;
        setConv(loaded);
        if (!id) setParams({ c: loaded.id }, { replace: true });
      })
      .catch((err: unknown) => live && setError(describe(err, 'stylist.loadFailed')));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // While a version is being shot or corrected, the conversation is read again until it is ready.
  const waiting = conv ? pending(conv) : false;
  useEffect(() => {
    if (!conv || !waiting) return;
    const timer = window.setTimeout(() => {
      void api.stylist
        .get(conv.id)
        .then(setConv)
        .catch(() => undefined);
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [conv, waiting]);

  const count = conv?.messages.length ?? 0;
  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [count, busy]);

  const act = async (what: string, run: (cid: string) => Promise<StylistConversation>) => {
    if (!conv || busy) return;
    setBusy(what);
    setError('');
    try {
      setConv(await run(conv.id));
    } catch (err) {
      setError(describe(err, 'stylist.failed'));
    } finally {
      setBusy(false);
    }
  };

  // The lens is kept as it is chosen; a short pause first, in case of a change of mind.
  const finishTimer = useRef<number | undefined>(undefined);
  const settings: ShootSettings = useMemo(
    () => ({ ...DEFAULT_SETTINGS, ...(conv?.settings ?? {}) }) as ShootSettings,
    [conv?.settings],
  );
  const setFinishes = (update: (prev: ShootSettings) => ShootSettings) => {
    if (!conv) return;
    const next = update(settings);
    const patch: Partial<StylistFinishes> = {
      aperture: next.aperture ?? 'auto',
      fidelity: next.fidelity === 'standard' ? 'standard' : 'max',
    };
    setConv({ ...conv, settings: { ...conv.settings, ...patch } });
    window.clearTimeout(finishTimer.current);
    finishTimer.current = window.setTimeout(() => {
      void api.stylist
        .finishes(conv.id, patch)
        .catch((err: unknown) => setError(describe(err, 'stylist.failed')));
    }, 400);
  };

  const say = (words: string) => {
    const said = words.trim();
    if (!said) return;
    setText('');
    void act('say', (cid) => api.stylist.say(cid, said));
  };

  const fresh = () => {
    setConv(undefined);
    setPicked([]);
    setParams({});
  };

  const perVersion = estimateMicros(1, { fidelity: settings.fidelity === 'standard' ? 'standard' : 'max' });
  const garments = useMemo(() => new Map([...wardrobe, ...picked].map((g) => [g.id, g])), [wardrobe, picked]);
  /** The wardrobe to pick from: what was just added first, then the rest. */
  const shelf = useMemo(
    () => [...picked.filter((g) => !wardrobe.some((w) => w.id === g.id)), ...wardrobe],
    [wardrobe, picked],
  );
  const latestOptions = useMemo(() => {
    const latest: Partial<Record<string, string>> = {};
    for (const m of conv?.messages ?? []) if (m.kind === 'options' && m.step) latest[m.step] = m.id;
    return latest;
  }, [conv?.messages]);

  if (!conv) {
    return (
      <div className="chat">
        <div className="chat__scroll">
          <div className="chat__thread">
            {error ? <div className="banner banner--error">{error}</div> : <Typing t={t} />}
          </div>
        </div>
      </div>
    );
  }

  const model = models.find((m) => m.id === conv.modelId);
  const fiche = FICHE.map((step) => {
    const value =
      step === 'model'
        ? model?.name
        : step === 'pieces'
          ? conv.garmentIds
              .map((gid) => pieceName(garments.get(gid)))
              .filter(Boolean)
              .join(' · ')
          : conv.chosen[step]?.title;
    const order = ['model', 'pieces', ...STEPS, 'review'];
    const state = value ? 'done' : order.indexOf(conv.step) === order.indexOf(step) ? 'now' : 'todo';
    return { step, value: value || t('stylist.toChoose'), state };
  });
  const jump = (step: Fiche) => {
    const target =
      step === 'model' || step === 'pieces'
        ? conv.messages.find((m) => m.kind === 'ask' && m.step === step)
        : conv.messages.find((m) => m.id === latestOptions[step]);
    if (target)
      document.getElementById(`m-${target.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const others = recent.filter((r) => r.id !== conv.id && r.versions > 0).slice(0, 4);
  const replies = quickReplies(conv, models, t);
  const placeholder = t(
    conv.step === 'model'
      ? 'stylist.say.model'
      : conv.step === 'pieces'
        ? 'stylist.say.pieces'
        : conv.step === 'review'
          ? 'stylist.say.review'
          : 'stylist.say.choose',
  );

  return (
    <div className="chat">
      <header className="chat__head">
        <div className="chat__headRow">
          <h1 className="chat__title">{t('stylist.title')}</h1>
          <nav className="chat__fiche" aria-label={t('stylist.fiche')}>
            {fiche.map((f) => (
              <button
                key={f.step}
                type="button"
                className={`chat__ficheItem is-${f.state}`}
                onClick={() => jump(f.step)}
                aria-current={f.state === 'now' ? 'step' : undefined}
              >
                <span>{t(`stylist.step.${f.step}`)}</span>
                <b>{f.value}</b>
              </button>
            ))}
          </nav>
          <div className="chat__headActions">
            {conv.shootId && (
              <Link className="btn btn--ghost btn--sm" to={`/shoots/${conv.shootId}`}>
                {t('stylist.openRoll')}
              </Link>
            )}
            <button type="button" className="btn btn--sm" onClick={fresh} aria-label={t('stylist.new')}>
              <PlusIcon />
              <span className="chat__hideSmall">{t('stylist.new')}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="chat__scroll">
        <div className="chat__thread" aria-live="polite">
          {conv.messages.map((m) =>
            m.role === 'client' ? (
              <ClientLine key={m.id} m={m} models={models} garments={garments} t={t} />
            ) : (
              <Stylist key={m.id} id={m.id} error={m.kind === 'error'} t={t}>
                {m.kind === 'ask' && m.step === 'model' && (
                  <>
                    <p className="chat__say">{t('stylist.ask.model')}</p>
                    {conv.step === 'model' && (
                      <div className="chat__models">
                        {models.map((x) => (
                          <button
                            key={x.id}
                            type="button"
                            className="chat__model"
                            disabled={Boolean(busy)}
                            onClick={() => void act('model', (cid) => api.stylist.model(cid, x.id))}
                          >
                            <span className="chat__modelPhoto">
                              {portraitOf(x) ? (
                                <img src={assetUrl(portraitOf(x)!)} alt="" />
                              ) : (
                                <b>{x.name.charAt(0)}</b>
                              )}
                            </span>
                            <span className="chat__modelName">{x.name}</span>
                            <span className="faint">
                              {t(x.house ? 'stylist.studioModel' : 'stylist.ownModel')}
                            </span>
                          </button>
                        ))}
                        <button
                          type="button"
                          className="chat__model chat__model--add"
                          onClick={() => setCreatingModel(true)}
                        >
                          <span className="chat__modelPhoto">
                            <PlusIcon />
                          </span>
                          <span className="chat__modelName">{t('stylist.createModel')}</span>
                        </button>
                      </div>
                    )}
                    {conv.step === 'model' && others.length > 0 && (
                      <div className="chat__resume">
                        <span className="faint">{t('stylist.resume')}</span>
                        {others.map((r) => (
                          <button
                            key={r.id}
                            type="button"
                            className="chip"
                            onClick={() => setParams({ c: r.id })}
                          >
                            {[r.chosen.outfit, r.chosen.place].filter(Boolean).join(' · ') ||
                              t('stylist.untitled')}{' '}
                            · {when.short(r.updatedAt)}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
                {m.kind === 'ask' && m.step === 'pieces' && (
                  <>
                    <p className="chat__say">{t('stylist.ask.pieces')}</p>
                    {conv.step === 'pieces' && (
                      <div className="chat__pieces">
                        <div className="chat__wardrobe">
                          <button
                            type="button"
                            className="chat__piece chat__piece--add"
                            onClick={() => setAdding(true)}
                          >
                            <span className="chat__piecePhoto">
                              <PlusIcon />
                            </span>
                            <span className="chat__pieceName">{t('stylist.addPiece')}</span>
                          </button>
                          {shelf.map((g) => {
                            const on = picked.some((x) => x.id === g.id);
                            return (
                              <button
                                key={g.id}
                                type="button"
                                className={`chat__piece${on ? ' is-on' : ''}`}
                                aria-pressed={on}
                                onClick={() =>
                                  setPicked((list) => (on ? list.filter((x) => x.id !== g.id) : [...list, g]))
                                }
                              >
                                <span className="chat__piecePhoto">
                                  <img src={assetUrl(g.assetId)} alt="" loading="lazy" />
                                  {on && (
                                    <span className="chat__pieceTick">
                                      <CheckIcon />
                                    </span>
                                  )}
                                </span>
                                <span className="chat__pieceName">{pieceName(g)}</span>
                              </button>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          className="btn btn--primary"
                          disabled={!picked.length || Boolean(busy)}
                          onClick={() =>
                            void act('pieces', (cid) =>
                              api.stylist.pieces(
                                cid,
                                picked.map((g) => g.id),
                              ),
                            )
                          }
                        >
                          {t('stylist.piecesDone')}
                        </button>
                      </div>
                    )}
                  </>
                )}
                {m.kind === 'options' && m.step && (
                  <Options
                    m={m}
                    conv={conv}
                    live={latestOptions[m.step] === m.id && reachable(conv, m.step as StylistStep)}
                    busy={busy}
                    cost={money.cost(perVersion)}
                    onChoose={(pick) =>
                      void act(`choose-${m.step}`, (cid) =>
                        api.stylist.choose(cid, m.step as StylistStep, pick),
                      )
                    }
                    onMore={() =>
                      void act(`more-${m.step}`, (cid) => api.stylist.more(cid, m.step as StylistStep))
                    }
                    t={t}
                  />
                )}
                {m.kind === 'version' && m.shotId && (
                  <Version
                    m={m}
                    shot={conv.frames[m.shotId]}
                    shootId={m.shootId}
                    settings={settings}
                    onDeveloped={(shot) =>
                      setConv((c) => (c ? { ...c, frames: { ...c.frames, [shot.id]: shot } } : c))
                    }
                    t={t}
                  />
                )}
                {(m.kind === 'text' || m.kind === 'error') && <p className="chat__say">{m.text}</p>}
                {m.kind === 'wait' && <p className="chat__say">{t('stylist.wait')}</p>}
                {m.kind === 'unclear' && <p className="chat__say">{t('stylist.unclear')}</p>}
              </Stylist>
            ),
          )}
          {busy && <Typing t={t} />}
          {error && <div className="banner banner--error">{error}</div>}
          <div ref={bottom} />
        </div>
      </div>

      <footer className="chat__foot">
        <div className="chat__footInner">
          {settingsOpen && (
            <div className="chat__settings">
              <div className="chat__settingsHead">
                <b>{t('stylist.finishes')}</b>
                <span className="faint">{t('stylist.costEach', { cost: money.cost(perVersion) })}</span>
              </div>
              <div className="wiz__finish">
                <ApertureField settings={settings} onChange={setFinishes} />
              </div>
            </div>
          )}
          {replies.length > 0 && (
            <div className="chat__replies">
              {replies.map((r) => (
                <button
                  key={r.label}
                  type="button"
                  className="chip"
                  disabled={Boolean(busy)}
                  onClick={() => {
                    if (r.run === 'model' && r.id) void act('model', (cid) => api.stylist.model(cid, r.id!));
                    else if (r.run === 'surprise' && r.id) {
                      // One of the stylist's own three, by chance.
                      const offered = conv.options[r.id as StylistStep] ?? [];
                      const pick = offered[Math.floor(Math.random() * offered.length)];
                      if (pick)
                        void act(`choose-${r.id}`, (cid) =>
                          api.stylist.choose(cid, r.id as StylistStep, { key: pick.key }),
                        );
                    } else if (r.run === 'more' && r.id)
                      void act(`more-${r.id}`, (cid) => api.stylist.more(cid, r.id as StylistStep));
                    else say(r.label);
                  }}
                >
                  {r.label}
                </button>
              ))}
            </div>
          )}
          <form
            className="chat__composer"
            onSubmit={(event) => {
              event.preventDefault();
              say(text);
            }}
          >
            <button
              type="button"
              className={`chat__tool${settingsOpen ? ' is-on' : ''}`}
              aria-pressed={settingsOpen}
              aria-label={t('stylist.finishes')}
              title={t('stylist.finishes')}
              onClick={() => setSettingsOpen((open) => !open)}
            >
              <SlidersIcon />
            </button>
            <textarea
              className="chat__input"
              rows={1}
              value={text}
              placeholder={placeholder}
              aria-label={placeholder}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  say(text);
                }
              }}
            />
            <button
              type="submit"
              className="chat__send"
              disabled={!text.trim() || Boolean(busy)}
              aria-label={t('stylist.send')}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 19V5M6 11l6-6 6 6" />
              </svg>
            </button>
          </form>
        </div>
      </footer>

      {adding && (
        <Sheet
          title={t('stylist.addPiece')}
          onClose={() => {
            setAdding(false);
            void api
              .listGarments()
              .then(setWardrobe)
              .catch(() => undefined);
          }}
        >
          <ClothesGuide garments={picked} onChange={setPicked} />
        </Sheet>
      )}

      {creatingModel && (
        <ModelEditor
          onClose={() => setCreatingModel(false)}
          onSaved={(message) => {
            setCreatingModel(false);
            toast(message, 'ok');
            void loadModels().then((list) => {
              const made = list.find((m) => !m.house);
              if (made) void act('model', (cid) => api.stylist.model(cid, made.id));
            });
          }}
          onChanged={() => void loadModels()}
          toast={toast}
          t={t}
        />
      )}
    </div>
  );
}

type Translate = ReturnType<typeof useT>;

/** What the client is offered to say with one tap, where the conversation is. */
function quickReplies(
  conv: StylistConversation,
  models: ModelWithStats[],
  t: Translate,
): Array<{ label: string; run: 'say' | 'model' | 'more' | 'surprise'; id?: string }> {
  if (conv.step === 'model')
    return models.slice(0, 3).map((m) => ({ label: m.name, run: 'model', id: m.id }));
  if (conv.step === 'pieces') return [];
  if (conv.step === 'review')
    return (['pose', 'place', 'blur', 'more'] as const).map((idea) => ({
      label: t(`stylist.idea.${idea}`),
      run: 'say',
    }));
  return [
    { label: t('stylist.surprise'), run: 'surprise', id: conv.step },
    { label: t('stylist.more'), run: 'more', id: conv.step },
  ];
}

/** A step can still be picked from: before the photograph, the current one; after it, any. */
function reachable(conv: StylistConversation, step: StylistStep): boolean {
  return conv.step === 'review' || conv.step === step;
}

/** Whether a version is still being shot or corrected. */
function pending(conv: StylistConversation): boolean {
  const frames = Object.values(conv.frames);
  if (frames.some((f) => f.status !== 'done' && f.status !== 'failed')) return true;
  const last = [...conv.messages].reverse();
  const correction = last.findIndex((m) => m.kind === 'version' && m.correction);
  if (correction < 0) return false;
  const m = last[correction]!;
  const said = last.slice(0, correction).some((x) => x.kind === 'error');
  const shot = m.shotId ? conv.frames[m.shotId] : undefined;
  return !said && Boolean(shot) && (shot!.takes?.length ?? 0) <= (m.take ?? 0);
}

function Stylist({
  id,
  error,
  t,
  children,
}: {
  id: string;
  error?: boolean;
  t: Translate;
  children: ReactNode;
}) {
  return (
    <div id={`m-${id}`} className={`chat__turn${error ? ' is-error' : ''}`}>
      <span className="chat__avatar" aria-hidden="true">
        S
      </span>
      <div className="chat__turnBody">
        <span className="chat__who">{t('stylist.name')}</span>
        {children}
      </div>
    </div>
  );
}

function Typing({ t }: { t: Translate }) {
  return (
    <div className="chat__turn">
      <span className="chat__avatar" aria-hidden="true">
        S
      </span>
      <span className="chat__typing" role="status" aria-label={t('stylist.typing')}>
        <i />
        <i />
        <i />
      </span>
    </div>
  );
}

function ClientLine({
  m,
  models,
  garments,
  t,
}: {
  m: StylistMessage;
  models: ModelWithStats[];
  garments: Map<string, Garment>;
  t: Translate;
}) {
  let body: ReactNode = m.text;
  if (m.kind === 'model') {
    const model = models.find((x) => x.id === m.modelId);
    body = (
      <span className="chat__clientItem">
        <Thumb assetId={model ? portraitOf(model) : undefined} />
        {model?.name ?? '…'}
      </span>
    );
  } else if (m.kind === 'pieces') {
    body = (m.garmentIds ?? []).map((gid) => (
      <span key={gid} className="chat__clientItem">
        <Thumb assetId={garments.get(gid)?.assetId} />
        {pieceName(garments.get(gid)) ?? '…'}
      </span>
    ));
  } else if (m.kind === 'choice' && m.option) {
    body = (
      <>
        <span className="chat__tag">{t(`stylist.step.${m.step}`)}</span> {m.option.title}
      </>
    );
  }
  return (
    <div id={`m-${m.id}`} className="chat__client">
      <div className="chat__bubble">{body}</div>
    </div>
  );
}

function Options({
  m,
  conv,
  live,
  busy,
  cost,
  onChoose,
  onMore,
  t,
}: {
  m: StylistMessage;
  conv: StylistConversation;
  live: boolean;
  busy: string | false;
  cost: string;
  onChoose: (pick: { key: string } | { other: string }) => void;
  onMore: () => void;
  t: Translate;
}) {
  const examples = useExamples();
  const [other, setOther] = useState('');
  const [open, setOpen] = useState(false);
  const step = m.step as StylistStep;
  const chosen = (m.options ?? []).find((o) => o.key === conv.chosen[step]?.key);
  const makes = conv.step === 'review' || step === 'pose';
  // Once a proposal is picked the cards fold into it; they open again on demand.
  const folded = Boolean(chosen) && !open;
  const picture = (o: StylistOption) =>
    o.assetId
      ? assetUrl(o.assetId)
      : o.presetId && examples[`scene-${o.presetId}`]
        ? exampleUrl(examples[`scene-${o.presetId}`]!)
        : undefined;

  return (
    <div className="chat__options">
      <p className="chat__say">{t(`stylist.options.${step}`)}</p>
      {folded && chosen ? (
        <div className="chat__picked">
          {picture(chosen) ? (
            <span className="chat__pickedThumb">
              <img src={picture(chosen)} alt="" />
            </span>
          ) : (
            <span className="chat__num">{(m.options ?? []).indexOf(chosen) + 1}</span>
          )}
          <b>{chosen.title}</b>
          {live && (
            <button type="button" className="chat__link" onClick={() => setOpen(true)}>
              {t('stylist.change')}
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="chat__cards">
            {(m.options ?? []).map((option, i) => {
              const src = picture(option);
              return (
                <button
                  key={option.key}
                  type="button"
                  className={`chat__card${src ? '' : ' is-text'}${chosen?.key === option.key ? ' is-on' : ''}`}
                  aria-pressed={chosen?.key === option.key}
                  disabled={!live || Boolean(busy)}
                  style={{ animationDelay: `${i * 70}ms` }}
                  onClick={() => {
                    setOpen(false);
                    onChoose({ key: option.key });
                  }}
                >
                  {src && (
                    <span className="chat__cardMedia">
                      <img src={src} alt="" loading="lazy" />
                    </span>
                  )}
                  <span className="chat__cardText">
                    <b>
                      <span className="chat__num">{i + 1}</span>
                      {option.title}
                    </b>
                    <span className="faint">{option.description}</span>
                  </span>
                </button>
              );
            })}
            {live && (
              <form
                className="chat__card chat__other"
                style={{ animationDelay: '210ms' }}
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!other.trim()) return;
                  setOpen(false);
                  onChoose({ other: other.trim() });
                  setOther('');
                }}
              >
                <b>{t('stylist.other')}</b>
                <textarea
                  className="textarea"
                  rows={2}
                  value={other}
                  placeholder={t(`stylist.otherHint.${step}`)}
                  aria-label={t(`stylist.otherHint.${step}`)}
                  onChange={(event) => setOther(event.target.value)}
                />
                <button type="submit" className="btn btn--sm" disabled={!other.trim() || Boolean(busy)}>
                  {t('stylist.otherGo')}
                </button>
              </form>
            )}
          </div>
          {live && (
            <div className="chat__optionsFoot">
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={Boolean(busy)}
                onClick={onMore}
              >
                {t('stylist.more')}
              </button>
              {makes && <span className="faint">{t('stylist.makesVersion', { cost })}</span>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Version({
  m,
  shot,
  shootId,
  settings,
  onDeveloped,
  t,
}: {
  m: StylistMessage;
  shot?: Shot;
  shootId?: string;
  settings: ShootSettings;
  onDeveloped: (shot: Shot) => void;
  t: Translate;
}) {
  const take = m.take ?? 0;
  const asset =
    shot?.takes?.[take]?.assetId ?? (take === 0 && shot?.status === 'done' ? shot.assetId : undefined);
  const failed = !asset && shot?.status === 'failed';
  // Only the photograph on show now can be developed: an earlier take of it is history.
  const developable = Boolean(shot && asset && shot.status === 'done' && asset === shot.assetId);
  return (
    <div className="chat__version">
      <div className={`chat__frame${asset ? '' : failed ? ' is-failed' : ' is-shooting'}`}>
        {asset ? (
          <Link to={`/shoots/${shootId}`} title={t('stylist.openRoll')}>
            <img
              className="chat__develop"
              src={assetUrl(asset)}
              alt={t('stylist.versionN', { n: m.n ?? 1 })}
            />
          </Link>
        ) : failed ? (
          <p>{shot?.error ?? t('stylist.failed')}</p>
        ) : (
          <p>{t(m.correction ? 'stylist.correcting' : 'stylist.shooting')}</p>
        )}
        <span className="chat__frameBadge">{t('stylist.versionN', { n: m.n ?? 1 })}</span>
      </div>
      <div className="chat__versionText">
        {m.correction ? (
          <span>
            <span className="chat__tag">{t('stylist.correction')}</span> {m.correction}
          </span>
        ) : (
          STEPS.map((step) => (
            <span key={step}>
              <span className="chat__tag">{t(`stylist.step.${step}`)}</span> {m.chosen?.[step]}
            </span>
          ))
        )}
        {developable && shot && <Develop shot={shot} settings={settings} onDeveloped={onDeveloped} t={t} />}
      </div>
    </div>
  );
}

/**
 * Film, grain and vignette, once the photograph is there to judge them on:
 * developed from its negative on the studio's machine, so trying one is free.
 */
function Develop({
  shot,
  settings,
  onDeveloped,
  t,
}: {
  shot: Shot;
  settings: ShootSettings;
  onDeveloped: (shot: Shot) => void;
  t: Translate;
}) {
  const tp = usePresetLabel();
  const describe = useErrorText();
  const [open, setOpen] = useState(false);
  const [stock, setStock] = useState(settings.filmStockId ?? NO_STOCK_ID);
  const [grain, setGrain] = useState(settings.grain ?? 0);
  const [vignette, setVignette] = useState(settings.vignette ?? 0);
  const [developing, setDeveloping] = useState(false);
  const [error, setError] = useState('');

  const develop = async () => {
    setDeveloping(true);
    setError('');
    try {
      onDeveloped(await api.develop(shot.id, stock, grain, vignette));
    } catch (err) {
      setError(describe(err, 'loupe.developError'));
    } finally {
      setDeveloping(false);
    }
  };

  if (!open)
    return (
      <button type="button" className="chat__link" onClick={() => setOpen(true)}>
        {t('stylist.develop')}
      </button>
    );
  return (
    <div className="chat__developPanel">
      <div className="chips">
        {FILM_STOCKS.map((option) => (
          <button
            key={option.id}
            type="button"
            className={`chip ${stock === option.id ? 'is-on' : ''}`}
            aria-pressed={stock === option.id}
            onClick={() => {
              setStock(option.id);
              setGrain(option.grain);
            }}
          >
            {tp(`stock.${option.id}.name`, option.name)}
          </button>
        ))}
      </div>
      {stock !== NO_STOCK_ID && (
        <Dial id={`grain-${shot.id}`} label={t('shoot.grain')} value={grain} onChange={setGrain} />
      )}
      <Dial id={`vignette-${shot.id}`} label={t('shoot.vignette')} value={vignette} onChange={setVignette} />
      <div className="chat__developFoot">
        <button type="button" className="btn btn--sm" disabled={developing} onClick={() => void develop()}>
          {developing ? <span className="spinner" /> : null}
          {t(developing ? 'loupe.developing' : 'loupe.developFrame')}
        </button>
        <span className="faint">{t('loupe.developFree')}</span>
      </div>
      {error && <p className="chat__developError">{error}</p>}
    </div>
  );
}

/** What a piece is called: the name it was given, unless that was only its photograph's file name. */
function pieceName(garment?: Garment): string | undefined {
  if (!garment) return undefined;
  const label = garment.label.trim();
  const fileLike = !/\s/.test(label) && /\d|^(tempimage|img|dsc|pxl|photo|image|screenshot)/i.test(label);
  return (!label || fileLike) && garment.analysis?.name ? garment.analysis.name : label;
}
